/**
 * 計測モード（地図に直線・円・多角形を描いて測る）の、DOMに依存しない部分。
 *
 * ★★ ここで出す数字は**飛行支援のための参考値**であって公式の計測値では
 *   ない（airspace.ts・lrn.ts と同じ方針）。
 *
 * 距離・方位は既存の `distanceMeters`/`bearingDeg`（goals.ts）をそのまま
 * 使う。UTM平面・**方眼北基準**で、ゴール一覧の距離・方位表示と同じ数字に
 * なる。面積は `polygonAreaKm2`（gv-core）。
 *
 * ★ 円は必ず `geoCircleRing` でポリゴン化すること。`circle`レイヤーの
 *   `circle-radius`によるズーム近似は実寸の約0.61倍に縮み、PZが危険側に
 *   小さく描かれた実機の不具合の原因になった（map/layers.ts のコメント参照）。
 */

import { polygonAreaKm2, polygonPerimeterM, toUtm, type MeasurePoint } from '@gv4d/core';
import { bearingDeg, distanceMeters } from '../goals.ts';
import { geoCircleRing } from '../map/layers.ts';

export interface MeasureLatLon {
  lat: number;
  lon: number;
}

/**
 * 計測の図形。`line`は連続した折れ線（2点だけ打てば単純な2点間計測）、
 * `circle`は中心＋半径、`polygon`は閉じた多角形。
 */
export type MeasureShape =
  | { kind: 'line'; id: string; points: MeasureLatLon[] }
  | { kind: 'circle'; id: string; center: MeasureLatLon; radiusM: number }
  | { kind: 'polygon'; id: string; points: MeasureLatLon[] };

export type MeasureTool = MeasureShape['kind'];

/** 保存する内容。図形そのものと、地図に出すかどうか（「消す」とは別の操作） */
export interface MeasureStore {
  shapes: MeasureShape[];
  visible: boolean;
}

export const EMPTY_MEASURE_STORE: MeasureStore = { shapes: [], visible: true };

function isLatLon(v: unknown): v is MeasureLatLon {
  const p = v as MeasureLatLon | null;
  return Number.isFinite(p?.lat) && Number.isFinite(p?.lon);
}

/**
 * 保存済みJSONを読み直す。1件の不備で全体を捨てないよう、図形ごとに
 * 独立して検証する（task-circles.ts の読み込みと同じ方針）。
 */
export function parseMeasureStore(raw: unknown): MeasureStore {
  const v = raw as Partial<MeasureStore> | null;
  if (!v || !Array.isArray(v.shapes)) return { ...EMPTY_MEASURE_STORE };
  const shapes: MeasureShape[] = [];
  for (const s of v.shapes) {
    const shape = s as MeasureShape | null;
    if (typeof shape?.id !== 'string') continue;
    if (shape.kind === 'circle') {
      if (isLatLon(shape.center) && Number.isFinite(shape.radiusM) && shape.radiusM > 0) {
        shapes.push({ kind: 'circle', id: shape.id, center: shape.center, radiusM: shape.radiusM });
      }
      continue;
    }
    if (shape.kind === 'line' || shape.kind === 'polygon') {
      const points = Array.isArray(shape.points) ? shape.points.filter(isLatLon) : [];
      if (points.length > 0) shapes.push({ kind: shape.kind, id: shape.id, points });
    }
  }
  return { shapes, visible: typeof v.visible === 'boolean' ? v.visible : true };
}

/** 緯度経度をUTM平面へ。面積・周囲長は必ずこちらで測る（緯度経度のままでは測らない） */
function toPlane(points: readonly MeasureLatLon[]): MeasurePoint[] {
  return points.map((p) => {
    const u = toUtm(p.lon, p.lat);
    return { e: u.easting, n: u.northing };
  });
}

/** ラベルの書式は既存に合わせる（goalBearingLineToGeoJson と同じ「方位° / 距離 km」） */
function segmentLabel(a: MeasureLatLon, b: MeasureLatLon): string {
  const km = distanceMeters(a, b) / 1000;
  return `${Math.round(bearingDeg(a, b))}° / ${km.toFixed(2)} km`;
}

/** 折れ線の合計距離 [m] */
export function lineTotalM(points: readonly MeasureLatLon[]): number {
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) total += distanceMeters(points[i], points[i + 1]);
  return total;
}

/** 円の面積 [km²]（πr²。ポリゴン近似ではなく厳密な円として出す） */
export function circleAreaKm2(radiusM: number): number {
  return (Math.PI * radiusM * radiusM) / 1_000_000;
}

/** ラベル用の代表点。真の重心ではなく頂点の平均（多角形が凹でも破綻しない） */
function averagePoint(points: readonly MeasureLatLon[]): MeasureLatLon {
  const lat = points.reduce((s, p) => s + p.lat, 0) / points.length;
  const lon = points.reduce((s, p) => s + p.lon, 0) / points.length;
  return { lat, lon };
}

/** カードに出す1行のまとめ */
export function measureSummary(shape: MeasureShape): string {
  if (shape.kind === 'circle') {
    return (
      `円: 半径 ${(shape.radiusM / 1000).toFixed(2)} km ／ ` +
      `面積 ${circleAreaKm2(shape.radiusM).toFixed(2)} km²`
    );
  }
  if (shape.kind === 'line') {
    const segments = Math.max(0, shape.points.length - 1);
    return `直線: ${segments}区間 ／ 合計 ${(lineTotalM(shape.points) / 1000).toFixed(2)} km`;
  }
  const plane = toPlane(shape.points);
  return (
    `面積: ${polygonAreaKm2(plane).toFixed(2)} km² ／ ` +
    `周囲 ${(polygonPerimeterM(plane, true) / 1000).toFixed(2)} km`
  );
}

/**
 * 図形を地図のGeoJSONへ。`role`でレイヤーを振り分ける:
 * - `fill` … 塗り（円・多角形のポリゴン）
 * - `seg` / `ring` … 線（`seg`は区間ごとで、そのままラベルにも使う）
 * - `vertex` … 頂点の丸（ドラッグで掴める点）
 * - `label` … 点に置く文字（円の半径・多角形の面積）
 */
export function measureShapesToGeoJson(shapes: readonly MeasureShape[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];

  const pushVertices = (shapeId: string, points: readonly MeasureLatLon[], role: string): void => {
    points.forEach((p, index) => {
      features.push({
        type: 'Feature',
        properties: { role: 'vertex', shapeId, index, vertexRole: role },
        geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
      });
    });
  };

  for (const shape of shapes) {
    if (shape.kind === 'circle') {
      const ring = geoCircleRing(shape.center.lat, shape.center.lon, shape.radiusM);
      features.push({
        type: 'Feature',
        properties: { role: 'fill', shapeId: shape.id },
        geometry: { type: 'Polygon', coordinates: [ring] },
      });
      features.push({
        type: 'Feature',
        properties: { role: 'ring', shapeId: shape.id },
        geometry: { type: 'LineString', coordinates: ring },
      });
      // 中心（index 0）と外周の1点（index 1）。中心をドラッグすると円ごと動き、
      // 外周をドラッグすると半径が変わる（掴んだ index で区別する）
      pushVertices(shape.id, [shape.center], 'center');
      features.push({
        type: 'Feature',
        properties: { role: 'vertex', shapeId: shape.id, index: 1, vertexRole: 'edge' },
        geometry: { type: 'Point', coordinates: ring[0] },
      });
      features.push({
        type: 'Feature',
        properties: { role: 'label', shapeId: shape.id, label: measureSummary(shape) },
        geometry: { type: 'Point', coordinates: [shape.center.lon, shape.center.lat] },
      });
      continue;
    }

    const { points } = shape;
    if (shape.kind === 'polygon' && points.length >= 3) {
      const ring = [...points.map((p): [number, number] => [p.lon, p.lat])];
      ring.push(ring[0]);
      features.push({
        type: 'Feature',
        properties: { role: 'fill', shapeId: shape.id },
        geometry: { type: 'Polygon', coordinates: [ring] },
      });
      features.push({
        type: 'Feature',
        properties: { role: 'ring', shapeId: shape.id },
        geometry: { type: 'LineString', coordinates: ring },
      });
      const center = averagePoint(points);
      features.push({
        type: 'Feature',
        properties: { role: 'label', shapeId: shape.id, label: measureSummary(shape) },
        geometry: { type: 'Point', coordinates: [center.lon, center.lat] },
      });
    } else {
      // 直線（と、まだ3点に満たない多角形）は区間ごとに線＋ラベルを出す
      for (let i = 0; i < points.length - 1; i++) {
        features.push({
          type: 'Feature',
          properties: { role: 'seg', shapeId: shape.id, label: segmentLabel(points[i], points[i + 1]) },
          geometry: {
            type: 'LineString',
            coordinates: [
              [points[i].lon, points[i].lat],
              [points[i + 1].lon, points[i + 1].lat],
            ],
          },
        });
      }
    }

    if (shape.kind === 'line') {
      // ★ 引いた向きが見た目で分かるよう、始点は○（中抜きの丸）・終点は→（矢印）にする。
      //   矢印の向きは直前の区間の進行方位（既存のbearingDegと同じ計算）
      points.forEach((p, index) => {
        const isEnd = index === points.length - 1 && points.length > 1;
        const vertexRole = isEnd ? 'end' : index === 0 ? 'start' : 'point';
        const properties: GeoJSON.GeoJsonProperties = { role: 'vertex', shapeId: shape.id, index, vertexRole };
        if (isEnd) properties.bearing = bearingDeg(points[index - 1], p);
        features.push({
          type: 'Feature',
          properties,
          geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
        });
      });
    } else {
      pushVertices(shape.id, points, 'point');
    }
  }

  return { type: 'FeatureCollection', features };
}
